// GET  /api/selfcare/plans    — sotib olish mumkin paketlar
// POST /api/selfcare/purchase — paket sotib olish (Idempotency-Key MAJBURIY)
//
// HAQIQIY backend kontrakti (`xsib-core/selfcare/internal/PlansController.java`,
// `PurchaseController.java`):
//   GET  /plans?lang=ru -> [{id, name, displayName, billingKind, price,
//                    quotaBytes, quotaSeconds, validityDays, deliveryMethod}]
//   POST /purchase {planId}, header Idempotency-Key
//        -> {grantId, planId, price, quotaBytes, quotaSeconds, expiresAt,
//            state, replay}
//
// ⚠️ Xarid HAQIQATAN ikkilik: muvaffaqiyatsizlik `status: FAILED` maydoni
// bilan EMAS, HTTP xato bilan keladi (masalan mablag' yetmasa 422
// `InsufficientFundsException`, plan sotuvda bo'lmasa 422
// `PlanNotAvailable`) — shuning uchun `purchasePlan` muvaffaqiyatsizlikda
// oddiy `throw` qiladi, chaqiruvchi `translateError` bilan ushlaydi.
// `PARTIAL` YO'Q: xaridda "faol sessiyaga darhol qo'llash" tushunchasi yo'q.
//
// `replay: true` — bu YANGI xarid emas, ESKI (bir xil Idempotency-Key bilan
// oldin qilingan) xarid natijasi qaytarildi. HTTP holati ham farqlanadi
// (201 yangi / 200 replay), lekin `replay` maydonining o'zi kifoya —
// alohida HTTP status tekshiruvi shart emas.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { toDeliveryMethod, type DeliveryMethod } from '@/shared/auth/authApi'
import { money, type Money } from '@/shared/lib/money'

/** `uz.xsib.catalog.api.BillingKind` */
export const BILLING_KINDS = ['PREPAID_TRAFFIC', 'PREPAID_TIME', 'UNLIMITED', 'POSTPAID'] as const
export type BillingKind = (typeof BILLING_KINDS)[number] | 'UNKNOWN'

function isBillingKind(value: string): value is (typeof BILLING_KINDS)[number] {
  return (BILLING_KINDS as readonly string[]).includes(value)
}

function toBillingKind(raw: string): BillingKind {
  const upper = raw.trim().toUpperCase()
  return isBillingKind(upper) ? upper : 'UNKNOWN'
}

/** `uz.xsib.subscriber.api.GrantState` */
export const GRANT_STATES = ['PENDING', 'ACTIVE', 'EXHAUSTED', 'EXPIRED', 'REVOKED'] as const
export type GrantState = (typeof GRANT_STATES)[number] | 'UNKNOWN'

function isGrantState(value: string): value is (typeof GRANT_STATES)[number] {
  return (GRANT_STATES as readonly string[]).includes(value)
}

function toGrantState(raw: string): GrantState {
  const upper = raw.trim().toUpperCase()
  return isGrantState(upper) ? upper : 'UNKNOWN'
}

const moneySchema = z.object({ amount: z.number(), currency: z.string() })

const planSchema = z.object({
  id: z.string(),
  name: z.string(),
  /**
   * Ekranga chiqadigan nom — server `?lang=` bo'yicha hal qilgan.
   *
   * ⚠️ `.nullish()`: panel va backend — IKKI mustaqil deploy artefakti.
   * Eski backend bu maydonni umuman yubormaydi va o'shanda `z.string()`
   * BUTUN sxemani yiqitardi, ya'ni abonent paketlar o'rniga xato
   * ko'rardi (bu tuzoq shu faylda `expiresAt` bilan bir marta
   * bo'lgan). Maydon yo'q bo'lsa kanonik nomga qaytamiz.
   */
  displayName: z.string().nullish(),
  billingKind: z.string(),
  price: moneySchema,
  quotaBytes: z.number().nullish(),
  quotaSeconds: z.number().nullish(),
  validityDays: z.number().nullish(),
  deliveryMethod: z.string(),
  /** Tezlik chegarasi (`"10M/10M"`) — katalogdagi siyosatdan, `null` = ko'rsatilmagan */
  rateLimit: z.string().nullish(),
})

const plansResponseSchema = z.array(planSchema)

export interface Plan {
  id: string
  /** Kanonik nom — chekda, jurnalda va qo'llab-quvvatlash yozishmasida shu turadi */
  name: string
  /** Ekranga chiqadigan nom. HECH QACHON bo'sh emas — tarjima yo'q bo'lsa `name` */
  displayName: string
  billingKind: BillingKind
  price: Money
  /** `null` — hajm bo'yicha cheksiz yoki bu reja hajm bilan o'lchanmaydi */
  quotaBytes: number | null
  /** `null` — vaqt bo'yicha cheksiz yoki bu reja vaqt bilan o'lchanmaydi */
  quotaSeconds: number | null
  /** `null` — muddat cheklanmagan */
  validityDays: number | null
  deliveryMethod: DeliveryMethod
  /** `"10M/10M"` shaklida. `null` — tezlik o'zgarmaydi (siyosat biriktirilmagan) */
  rateLimit: string | null
}

/**
 * Paketlar ro'yxati — ABONENT TANLAGAN tilda.
 *
 * ⚠️ `lang` ARGUMENT, va bu ataylab. Uch qatlamda o'lchangan sabab:
 *
 *  1. Brauzerning `Accept-Language` i bu yerda ish bermaydi — u
 *     OS/brauzer tili, kabinetda esa abonent tilni O'ZI tanlaydi
 *     (`app/i18n`, `localStorage`). Ular tez-tez mos kelmaydi.
 *  2. Sarlavha bo'lganda ham u KATALOGGACHA yetib bormasdi: selfcare →
 *     catalog chaqiruvi servis tokeni bilan ketadi va sarlavha o'sha
 *     yerda to'xtaydi (`CatalogClient` javadoc'i).
 *  3. Argument unutilmaydi — TypeScript uni talab qiladi. Ixtiyoriy
 *     qilinsa, chaqiruvchi uni tashlab ketardi va natija JIMGINA
 *     kanonik nom bo'lardi: paketlar ko'rinadi, xato yo'q, faqat
 *     nomlar boshqa tilda. Aynan shu XSB-007 edi.
 *
 * ⚠️ Chaqiruvchi `queryKey` ga ham SHU tilni qo'shishi shart, aks
 * holda til almashtirilganda kesh eski javobni qaytaradi va ekran
 * yangilanmaydi.
 */
export async function fetchPlans(lang: string): Promise<Plan[]> {
  const response = await api.get<unknown>('/api/selfcare/plans', { params: { lang } })
  const parsed = plansResponseSchema.parse(response.data)
  return parsed.map((p) => ({
    id: p.id,
    name: p.name,
    // Tarjima yo'q bo'lsa kanonik nom — bo'sh sarlavha CHIQMAYDI.
    // (`?.trim() ||` ataylab: eski backend maydonni umuman yubormasligi
    // ham, bo'sh satr yuborishi ham bir xil natija berishi kerak.)
    displayName: p.displayName?.trim() || p.name,
    billingKind: toBillingKind(p.billingKind),
    price: money(p.price.amount, p.price.currency),
    quotaBytes: p.quotaBytes ?? null,
    quotaSeconds: p.quotaSeconds ?? null,
    validityDays: p.validityDays ?? null,
    deliveryMethod: toDeliveryMethod(p.deliveryMethod),
    rateLimit: p.rateLimit ?? null,
  }))
}

/**
 * Paket abonentning ulanish turiga MOS KELMAYAPTIMI.
 *
 * <h2>⚠️ NEGA BU KERAK — «noto'g'ri paket to'g'risidan farq qilmasdi»</h2>
 *
 * `PlansController` ro'yxatni FAQAT `sellable` bo'yicha filtrlaydi
 * (`catalog.availableFor(...).filter(Plan::sellable)`) — ulanish turi
 * bo'yicha EMAS. Ya'ni Hotspot abonenti PPPoE paketini ro'yxatda
 * ko'radi va uni sotib ola oladi.
 *
 * Kartada esa bu farq HECH QANDAY signal bermasdi: `deliveryMethod`
 * neytral kulrang teg bo'lib turardi, ya'ni mos paket ham, mos
 * kelmaydigan paket ham AYNAN bir xil ko'rinardi — buzuq narsa tinch
 * rangda turardi.
 *
 * Oqibati pulga tegadi: xarid o'tadi, balansdan yechiladi, internet esa
 * ochilmaydi. `PlansPage` ning o'z izohi buni ALLAQACHON talab qilgan
 * ("PPPoE paketini Hotspot abonenti sotib olishi ma'nosiz va buni
 * xarid oldidan ko'rish kerak"), lekin talab kodda bajarilmagan edi.
 *
 * <h2>⚠️ Nega bu yerda, ekranda emas</h2>
 * Bu DOMEN qoidasi, ko'rinish emas — va u SINALADIGAN bo'lishi kerak.
 * JSX ichida qolganda uni faqat manba matnidan o'qib "tekshirish"
 * mumkin edi, ya'ni qorovul xulqni emas, yozuvni o'lchagan bo'lardi.
 *
 * <h2>⚠️ Xarid TO'SILMAYDI</h2>
 * Faqat ogohlantiriladi. Sabab: operator ataylab shunday sotayotgan
 * bo'lishi mumkin (abonent turi o'zgarayotgan bo'lsa), va panel
 * backendning qaroriga qarshi turmasligi kerak. Bizning ishimiz —
 * abonent buni PUL yechilishidan OLDIN ko'rsin.
 *
 * ⚠️ `UNKNOWN` bo'lsa ogohlantirish CHIQMAYDI: noma'lum qiymat
 * "mos emas" degani emas, va noto'g'ri signal to'g'ri paketni sotib
 * olishdan qaytarardi.
 */
export function isMethodMismatch(
  plan: Pick<Plan, 'deliveryMethod'>,
  subscriberKind: DeliveryMethod,
): boolean {
  if (subscriberKind === 'UNKNOWN' || plan.deliveryMethod === 'UNKNOWN') return false
  return plan.deliveryMethod !== subscriberKind
}

/**
 * Xarid uchun balansda QANCHA PUL YETMAYAPTI.
 *
 * `null` — uch xil holatni bildiradi va uchalasida ham ekranda son
 * KO'RSATILMASLIGI kerak:
 *   · pul yetadi;
 *   · balans hali o'qilmagan (`undefined`/`null`);
 *   · valyutalar boshqa.
 *
 * <h2>⚠️ Nega valyuta mos kelmasa `null`, nolga tenglashtirilmaydi</h2>
 * Turli valyutani ayirish — jimgina yolg'on son berardi (`money.ts`
 * dagi `addMoney` aynan shu sababdan istisno otadi). Kurs vaqt bo'yicha
 * o'zgaradi va uni panelda taxmin qilish mumkin emas. Bunday holatda
 * "yetishmayapti" degan aniq raqam o'rniga umumiy, LEKIN rost xabar
 * beriladi.
 *
 * <h2>⚠️ Nega bu yerda, ekranda emas</h2>
 * Bu pul qoidasi va u SINALADIGAN bo'lishi kerak: JSX ichida qolganda
 * qorovul xulqni emas, yozuvni o'lchagan bo'lardi (`isMethodMismatch`
 * bilan bir xil sabab).
 */
export function purchaseShortfall(price: Money, balance: Money | null | undefined): Money | null {
  if (!balance) return null
  if (balance.currency !== price.currency) return null
  const missing = price.amount - balance.amount
  return missing > 0 ? money(missing, price.currency) : null
}

const purchaseResponseSchema = z.object({
  grantId: z.string(),
  planId: z.string(),
  price: moneySchema,
  quotaBytes: z.number().nullish(),
  quotaSeconds: z.number().nullish(),
  /**
   * ⚠️ `.nullish()` — VA BU NUQSON ABONENTGA YOLG'ON XATO KO'RSATARDI.
   *
   * Bu yerda `z.string()` MAJBURIY deb yozilgan edi. `PurchaseResponse.expiresAt`
   * esa `Instant` va u MUDDATSIZ paketda `null` bo'ladi — Jackson `non_null`
   * bilan sozlangani uchun bunday maydon JSON'da UMUMAN bo'lmaydi.
   *
   * Sinov muhitida BARCHA yetti paketning `validity_days` i `NULL` edi,
   * ya'ni har bir xarid shu yo'ldan o'tardi: server pulni yechardi, grant
   * yaratardi va 201 qaytarardi — panel esa `zod` istisnosi bilan
   * yiqilib, ekranda XATO ko'rsatardi. Abonent "sotib olinmadi" deb
   * o'ylab qayta bosardi (ikkinchi marta `Idempotency-Key` boshqa
   * bo'lgani uchun IKKINCHI paket sotib olinardi).
   */
  expiresAt: z.string().nullish(),
  state: z.string(),
  replay: z.boolean(),
})

export interface PurchaseResult {
  grantId: string
  planId: string
  price: Money
  quotaBytes: number | null
  quotaSeconds: number | null
  /** `null` — paket muddatsiz (`validityDays` berilmagan) */
  expiresAt: string | null
  state: GrantState
  /** `true` — bu takroriy so'rov, YANGI xarid qilinmadi (bir xil Idempotency-Key) */
  replay: boolean
}

export interface PurchaseRequest {
  planId: string
  /** `shared/lib/idempotency.ts` — takroriy bosishdan himoya, MAJBURIY header */
  idempotencyKey: string
}

export async function purchasePlan(request: PurchaseRequest): Promise<PurchaseResult> {
  const response = await api.post<unknown>(
    '/api/selfcare/purchase',
    { planId: request.planId },
    { headers: { 'Idempotency-Key': request.idempotencyKey } },
  )
  const parsed = purchaseResponseSchema.parse(response.data)
  return {
    grantId: parsed.grantId,
    planId: parsed.planId,
    price: money(parsed.price.amount, parsed.price.currency),
    quotaBytes: parsed.quotaBytes ?? null,
    quotaSeconds: parsed.quotaSeconds ?? null,
    expiresAt: parsed.expiresAt ?? null,
    state: toGrantState(parsed.state),
    replay: parsed.replay,
  }
}
